import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import styles from './csv-import-modal-shell.css?inline';
import { STEPS, stepIndex, type StepId } from '../../constants/steps';

import '@inmar/component-lib';

// ============================================================================
// Module-level constants
// ============================================================================

/** Pre-computed stepper step labels — stable reference, never re-allocated. */
const STEPPER_STEPS = STEPS.map((s) => ({ label: s.label }));

// ============================================================================
// Types
// ============================================================================

// (No exported event types — intent events carry empty detail per architecture doc)

// ============================================================================
// Global augmentations
// ============================================================================

declare global {
  interface HTMLElementTagNameMap {
    'csv-import-modal-shell': CsvImportModalShell;
  }
}

// ============================================================================
// Component
// ============================================================================

/**
 * Shared modal chrome for the CSV Import multi-step flow.
 *
 * Renders `<inmar-hc-modal>` (always open — the orchestrator controls
 * visibility by conditionally rendering this element). Once mounted, the
 * shell stays in the DOM across step transitions to avoid dialog remount
 * flashes. It is unmounted when the workflow closes.
 *
 * @fires csv-import-close - Fired when the user clicks the X button.
 *
 * @slot step-body - Step body content (setup-body, review-body, confirm-body).
 * @slot footer    - Footer buttons (Next/Back/Cancel/Submit per step).
 */
@customElement('csv-import-modal-shell')
export class CsvImportModalShell extends LitElement {
  static readonly styles = [unsafeCSS(styles)];

  // ============================================================================
  // Properties
  // ============================================================================

  /** Title text displayed in the modal header. */
  @property({ type: String, attribute: 'modal-title' })
  modalTitle = '';

  /** The current step identifier. Converted to 1-based index for the stepper. */
  @property({ type: String, attribute: 'current-step' })
  currentStep: StepId = 'select-file';

  // ============================================================================
  // Event handlers
  // ============================================================================

  private _onInnerModalClose(e: Event) {
    e.preventDefault();
    this.dispatchEvent(
      new CustomEvent('csv-import-close', {
        bubbles: true,
        composed: true,
      }),
    );
  }

  // ============================================================================
  // Render
  // ============================================================================

  override render() {
    return html`
      <inmar-hc-modal
        .open=${true}
        modal-title=${this.modalTitle}
        size="lg"
        .backdropDismiss=${false}
        @close=${this._onInnerModalClose}
      >
        <div slot="body">
          <div class="stepper-container">
            <inmar-hc-stepper
              .steps=${STEPPER_STEPS}
              current-step=${stepIndex(this.currentStep)}
            ></inmar-hc-stepper>
          </div>
          <slot name="step-body"></slot>
        </div>

        <slot name="footer" slot="footer"></slot>
      </inmar-hc-modal>
    `;
  }
}
